export const communityMapRequest = async (path, signal) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8',
            Authorization: sessionStorage.getItem('agronetToken'),
            Local: localStorage.getItem('lang') || 'en'
        },
        signal
    });
    if (!response.ok) throw new Error(String(response.status));
    return response.json();
};

/** Local `datetime-local` value -> UTC ISO string with Z (seconds precision). */
export const toUtcParam = localValue => {
    const date = new Date(localValue);
    if (!localValue || !Number.isFinite(date.getTime())) {
        throw new Error('Choose a valid date and time.');
    }
    return `${date.toISOString().slice(0, 19)}Z`;
};

/** UTC ISO string -> local `datetime-local` value. */
export const utcToLocalInput = utcValue => {
    const date = new Date(utcValue);
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export const buildMomentQuery = (localValue, level) => {
    const at = toUtcParam(localValue);
    if (new Date(at) > new Date()) {
        throw new Error('The moment must be in the past.');
    }
    return `/panel/community_map/history?at=${encodeURIComponent(at)}&levels=${level}&includeSensors=true`;
};

export const buildSeriesQuery = ({from, to, stepMinutes, level}, maxSteps) => {
    const fromUtc = toUtcParam(from);
    const toUtc = toUtcParam(to);
    const start = new Date(fromUtc);
    const end = new Date(toUtc);
    if (end <= start) {
        throw new Error('The end must be after the start.');
    }
    if (end > new Date()) {
        throw new Error('The period must be in the past.');
    }
    const step = Number(stepMinutes);
    if (!Number.isInteger(step) || step < 5) {
        throw new Error('The step must be at least 5 minutes.');
    }
    const steps = Math.floor((end - start) / 60000 / step) + 1;
    if (steps > maxSteps) {
        throw new Error(`Too many steps (${steps}), maximum is ${maxSteps}.`);
    }
    return `/panel/community_map/history/series?from=${encodeURIComponent(fromUtc)}&to=${encodeURIComponent(toUtc)}`
        + `&stepMinutes=${step}&level=${level}`;
};

const frostScale = [
    [-4, '#313695'], [-2, '#4575b4'], [0, '#74add1'], [2, '#abd9e9'], [4, '#e0f3f8'],
    [8, '#fee090'], [14, '#fdae61'], [20, '#f46d43']
];

/** Colour scale centred on frost: blues below +4 °C, warm colours above. */
export const frostColor = value => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '#9e9e9e';
    for (const [limit, color] of frostScale) {
        if (value <= limit) return color;
    }
    return '#d73027';
};

export const frostLegend = [
    {label: '≤ −4 °C', color: '#313695'}, {label: '−4 … −2', color: '#4575b4'}, {label: '−2 … 0', color: '#74add1'},
    {label: '0 … 2', color: '#abd9e9'}, {label: '2 … 4', color: '#e0f3f8'}, {label: '4 … 8', color: '#fee090'},
    {label: '8 … 14', color: '#fdae61'}, {label: '14 … 20', color: '#f46d43'}, {label: '> 20 °C', color: '#d73027'}
];

const statusStyles = {
    INCLUDED: {color: '#22bf00', label: 'Included'},
    EXCLUDED_OUTLIER: {color: '#d91212', label: 'Outlier'},
    EXCLUDED_PROBATION: {color: '#ff8c00', label: 'Probation'},
    EXCLUDED_UNKNOWN: {color: '#7b61ff', label: 'Unknown (excluded)'},
    DROPPED_LIVE: {color: '#c2185b', label: 'Dropped at this moment'},
    STALE: {color: '#8d6e63', label: 'Stale reading'},
    NO_DATA: {color: '#9e9e9e', label: 'No data'}
};

export const sensorStatusStyle = status => statusStyles[status] || {color: '#9e9e9e', label: status || 'Unknown'};

export const sensorStatusOrder = Object.keys(statusStyles);

export const communityMapLabels = {
    CLEAN: 'Clean', OUTLIER: 'Outlier', PROBATION: 'Probation', UNKNOWN: 'Unknown',
    OK: 'OK', MIN: 'Lowest', MEDIAN: 'Median',
    NEW_OUTLIER: 'New outlier', STILL_OUTLIER: 'Still outlier', NO_ASSESSMENT: 'No assessment today',
    NO_HISTORY_UNKNOWN: 'Not assessed yet', REHABILITATION_STARTED: 'Rehabilitation started',
    PROBATION_CONTINUES: 'Probation continues', REHABILITATED: 'Rehabilitated', MEMORY_EXPIRED: 'Outlier memory expired',
    NOT_TRACKING: 'Does not follow the weather', LOW_AMPLITUDE: 'Daily range too small',
    LOW_TEST_POWER: 'Weather too flat to prove repair', CONVERGENCE_SUSPECTED: 'Convergence suspected',
    STUCK_VALUES: 'Stuck values', NIGHT_OFFSET: 'Night offset', NIGHT_SPREAD: 'Erratic at night',
    LOW_CORRELATION: 'Low correlation', INDOOR_PATTERN: 'Indoor pattern', INSUFFICIENT_DATA: 'Insufficient data',
    NO_REFERENCE: 'No reference', SPIKE_REPLACED: 'Spike replaced', STALE: 'Stale', NO_DATA: 'No data'
};

export const label = (t, value) => t.gettext(communityMapLabels[value] || value || '—');

/** Corners [lat, lon] of a map cell: a pointy-top hexagon when the id starts with H, otherwise its box. */
export const cellOutline = cell => {
    const [south, west, north, east] = cell.bounds;
    if (String(cell.id).startsWith('H')) {
        const quarter = (north - south) / 4;
        const middle = (west + east) / 2;
        return [
            [north, middle],
            [north - quarter, east],
            [south + quarter, east],
            [south, middle],
            [south + quarter, west],
            [north - quarter, west]
        ];
    }
    return [[south, west], [south, east], [north, east], [north, west]];
};
